import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, MessageSquareText, RefreshCw } from 'lucide-react';
import { fetchAddressComments } from '@/hooks/useAddressValidation';
import type { AddressCommentItem } from '@uniticket/shared';

// Les commentaires d'une adresse dans la console DMP (demande du user, 22-10 : « quand un commentaire existe pour une
// adresse, je veux que ça s'affiche dans la console DMP »). Ce sont les motifs obligatoires des gestes d'exception
// (colonne corrigée, statut corrigé, « Forcer », « Délier »), qui n'étaient lisibles que dans la base.
//
// Le bloc n'apparaît QUE s'il y a au moins un commentaire : une adresse sans histoire ne gagne pas un cadre vide.
// Une lecture qui échoue, elle, se dit : se taire laisserait croire qu'aucun motif n'existe.

const fmt = (iso: string) => new Date(iso).toLocaleString('fr-BE', { dateStyle: 'short', timeStyle: 'short' });
const val = (v: string | null) => (v == null || v === '' ? 'NULL' : v);

export function AddressCommentsBlock({ jvid }: { jvid: number }) {
  const { t } = useTranslation(['tickets']);
  const [items, setItems] = useState<AddressCommentItem[] | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      setItems((await fetchAddressComments(jvid)).data);
    } catch {
      setItems(null);
      setFailed(true);
    }
  }, [jvid]);
  useEffect(() => { void load(); }, [load]);

  if (failed) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5" role="alert">
        <p className="flex items-start gap-1.5 text-[11px] font-medium text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {t('tickets:dmpLookup.comments.loadError')}
        </p>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-1 rounded-full border border-amber-300 px-2.5 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {t('tickets:dmpLookup.comments.retry')}
        </button>
      </div>
    );
  }
  if (!items || items.length === 0) return null;

  return (
    <div>
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <MessageSquareText className="h-3.5 w-3.5" />
        {t('tickets:dmpLookup.comments.title')}
        <span className="font-normal normal-case text-slate-400">· {t('tickets:dmpLookup.comments.count', { count: items.length })}</span>
      </p>
      <ul className="space-y-2">
        {items.map((c) => (
          <li key={c.id} className="rounded-xl border border-sky-200 bg-sky-50/60 px-3 py-2.5">
            <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{c.text}</p>
            <p className="mt-1 text-[11px] text-slate-500">
              <span className="font-semibold text-slate-600">{t(`tickets:dmpLookup.comments.kind.${c.kind}`)}</span>
              {c.column && (
                <span className="font-mono"> · {c.column} : {val(c.before)} → {val(c.after)}</span>
              )}
              {c.ticketNumber && <span> · {c.ticketNumber}</span>}
              <span> · {c.by ?? '—'} · {fmt(c.createdAt)}</span>
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
